import React, { useEffect, useRef, useState, type ReactNode } from 'react'

import type { FitKey } from '@vp/lat-react-component-library'

import type { Project } from '../../../../types/Project'
import type { SizesData } from '../../../../types/QuantitySelectionData'
import type { Work } from '../../../../types/Work'
import { useProjects } from '../ProjectsContext'
import { useWorksDataDispatch } from '../WorksDataContext'
import { deleteWork, deleteWorkStart, restoreWork } from '../WorksDataContext/WorkDataAction'
import { useAlertState } from '../AlertContext'
import { TrackingLabel } from '../TrackingContext/TrackingConstants'
import { useTracking } from '../TrackingContext'

export interface IProjectContextModel {
  workId: string
  work: Work
  productName: string
  productKey: string
  productVersion: number
  mpvId: string
  minimumOrderQuantity: number
  maximumOrderQuantity?: number
  quantityIncrement?: number
  lowestPriceAttributes: Record<string, string>
  sizes: SizesData | null
  sizeFitKey: FitKey | null
  isSizedGood: boolean
  singleValueSize?: string

  /**
   * The position of this project in the list of works
   */
  projectNumber: number
  /**
   * The total number of project loaded into QSP
   */
  projectCount: number
  removeProject: () => void
  abortRemoveProject: () => void
  confirmRemoveProject: () => void
  isPendingRemoval: boolean
  isPrimaryProject: boolean

  productViewTracked: boolean
  setProductViewTracked: (value: boolean) => void
}

export const ProjectContext = React.createContext({} as IProjectContextModel)

const ProjectContextProvider: React.FC<{
  project: Project
  projectNumber: number
  projectCount: number
  children: ReactNode
}> = ({ project, projectNumber, projectCount, children }) => {
  const [isPendingRemoval, setIsPendingRemoval] = useState<boolean>(false)
  const [productViewTracked, setProductViewTracked] = useState(false)

  const { removeWorkId } = useProjects()
  const worksDataDispatch = useWorksDataDispatch()
  const { projectQtyErrors, setProjectQtyErrors } = useAlertState()
  const { trackProductRemoved, trackProductUndo } = useTracking()

  const timer = useRef<number | undefined>(undefined)

  useEffect(() => {
    return () => {
      if (timer.current) {
        window.clearTimeout(timer.current)
      }
    }
  }, [])
  const singleValueSize = project.pageMode === 'One Sized Good' ? `${project.sizes?.name}: ${project.sizes?.values[0]?.name}` : undefined
  const isSizedGood = project.pageMode === 'Sized Good'

  const removeProject = () => {
    setIsPendingRemoval(true)
    worksDataDispatch(deleteWorkStart(project.workId))
    // maybe just ignore the project in calculation
    timer.current = window.setTimeout(() => {
      confirmRemoveProject()
    }, 5000)
  }

  const abortRemoveProject = () => {
    setIsPendingRemoval(false)
    worksDataDispatch(restoreWork(project.workId))
    window.clearTimeout(timer.current)

    const { mpvId, productName, productKey, productVersion } = project
    trackProductUndo(TrackingLabel.RELATED_PRODUCT_UNDO, mpvId, productName, productKey, productVersion)
  }

  const confirmRemoveProject = () => {
    /**
     * The customer confirmed the project should be
     * removed by dismissing the alert toast
     */
    window.clearTimeout(timer.current)
    worksDataDispatch(deleteWork(project.workId))

    const { mpvId, productName, productKey, productVersion } = project
    trackProductRemoved(TrackingLabel.RELATED_PRODUCT_REMOVED, mpvId, productName, productKey, productVersion)

    if (projectQtyErrors.includes(project.workId)) {
      // remove workId from projectQtyErrors to avoid any invalid quantity errors
      // that persist after adding a valid quantity to a work, removing it, and
      // then re-adding it to the page.
      const newProjectQtyErrors = projectQtyErrors.filter((workId) => workId !== project.workId)
      setProjectQtyErrors(newProjectQtyErrors)
    }
    removeWorkId(project.workId)
  }

  if (!project) {
    return <></>
  }

  return (
    <ProjectContext.Provider
      value={{
        workId: project.workId,
        work: project.work,
        productName: project.productName,
        productKey: project.productKey,
        productVersion: project.productVersion,
        mpvId: project.mpvId,
        minimumOrderQuantity: project.minimumOrderQuantity,
        maximumOrderQuantity: project.maximumOrderQuantity,
        quantityIncrement: project.quantityIncrement,
        lowestPriceAttributes: project.lowestPriceAttributes,
        sizes: project.sizes,
        sizeFitKey: project.sizeFitKey,
        isSizedGood,
        singleValueSize,
        projectNumber,
        projectCount,
        removeProject,
        abortRemoveProject,
        confirmRemoveProject,
        isPendingRemoval,
        isPrimaryProject: projectNumber === 1,
        productViewTracked,
        setProductViewTracked,
      }}
    >
      {children}
    </ProjectContext.Provider>
  )
}

export default ProjectContextProvider
